import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, Copy, FlaskConical, XCircle } from "lucide-react";
import { ApiError } from "../../../lib/api";
import { formatMoney } from "../../../lib/money";
import { approveTestPayment, startOrderPayment, usePublicOrder } from "../../../lib/store-api";
import { useStorefrontContext } from "./StorefrontContext";

const STATUS_LABEL: Record<string, string> = {
  pending: "Awaiting payment",
  paid: "Paid",
  fulfilled: "On its way",
  cancelled: "Cancelled",
};

function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="zs-copy">
      <span className="zs-muted">{label}</span>
      <strong>{value}</strong>
      <button
        type="button"
        className="zs-link-btn"
        aria-label={`Copy ${label.toLowerCase()}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => setCopied(true));
        }}
      >
        <Copy size={14} aria-hidden="true" />
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function OrderPage({ orderId }: { orderId: string }) {
  const { store, slug, paths, go } = useStorefrontContext();
  const [params] = useSearchParams();
  const token = params.get("token");
  const isTestPayment = params.get("testPayment") === "1";
  const queryClient = useQueryClient();
  const { data: order, isPending, error } = usePublicOrder(slug, orderId, token);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Order confirmation · ${store.name}`;
  }, [store.name]);

  if (isPending && token) {
    return <section className="zs-page zs-empty">Loading your order…</section>;
  }
  if (!token || error || !order) {
    return (
      <section className="zs-page zs-empty">
        <XCircle size={36} aria-hidden="true" />
        <h1 className="zs-title">Order not found</h1>
        <p>Check the link in your confirmation email.</p>
        <button type="button" className="zs-btn" onClick={() => go(paths.shop)}>
          Continue shopping
        </button>
      </section>
    );
  }

  const pending = order.status === "pending";
  const cancelled = order.status === "cancelled";

  const runAction = async (fn: () => Promise<void>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="zs-page zs-order">
      <div className="zs-order__hero">
        <span className={`zs-order__icon${pending ? " zs-order__icon--pending" : ""}`} aria-hidden="true">
          {cancelled ? <XCircle size={28} /> : pending ? <Clock size={28} /> : <CheckCircle2 size={28} />}
        </span>
        <p className="zs-eyebrow">Order {order.number}</p>
        <h1 className="zs-title">
          {cancelled
            ? "This order was cancelled"
            : pending
              ? `Thank you, ${order.customer.name.split(" ")[0]}!`
              : `Thank you, ${order.customer.name.split(" ")[0]} — your order is confirmed`}
        </h1>
        <p className="zs-muted">
          {pending && order.paymentMethod === "bank_transfer"
            ? "Your order is reserved. Complete the transfer below and we'll confirm it by email."
            : pending
              ? "Your order is reserved and waiting for payment."
              : `A confirmation has been sent to ${order.customer.email}.`}
        </p>
        <span className={`zs-status zs-status--${order.status}`}>{STATUS_LABEL[order.status] ?? order.status}</span>
      </div>

      {pending && order.bankDetails ? (
        <div className="zs-panel zs-bank">
          <h2 className="zs-subtitle">Pay by bank transfer</h2>
          <p className="zs-muted">Transfer the exact amount and use your order number as the reference.</p>
          <CopyValue label="Amount" value={formatMoney(order.totalMinor, order.currency)} />
          <CopyValue label="Bank" value={order.bankDetails.bankName} />
          <CopyValue label="Account name" value={order.bankDetails.accountName} />
          <CopyValue label="Account number" value={order.bankDetails.accountNumber} />
          <CopyValue label="Reference" value={order.number} />
        </div>
      ) : null}

      {pending && order.paymentMethod === "online" ? (
        <div className="zs-panel zs-bank">
          {isTestPayment ? (
            <>
              <h2 className="zs-subtitle">
                <FlaskConical size={18} aria-hidden="true" /> Test payment
              </h2>
              <p className="zs-muted">
                This store is in test mode, so no real money moves. Approve to simulate a successful payment.
              </p>
              <button
                type="button"
                className="zs-btn"
                disabled={busy}
                onClick={() =>
                  runAction(async () => {
                    const paid = await approveTestPayment(slug, order.id, token);
                    queryClient.setQueryData(["public-order", slug, orderId, token], paid);
                  })
                }
              >
                Approve test payment of {formatMoney(order.totalMinor, order.currency)}
              </button>
            </>
          ) : (
            <>
              <h2 className="zs-subtitle">Complete your payment</h2>
              <p className="zs-muted">If you closed the payment page, you can continue here.</p>
              <button
                type="button"
                className="zs-btn"
                disabled={busy}
                onClick={() =>
                  runAction(async () => {
                    const { paymentUrl } = await startOrderPayment(slug, order.id, token);
                    window.location.assign(paymentUrl);
                  })
                }
              >
                Pay {formatMoney(order.totalMinor, order.currency)}
              </button>
            </>
          )}
          {actionError ? (
            <p className="zs-error" role="alert">
              {actionError}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="zs-panel">
        <h2 className="zs-subtitle">Order details</h2>
        <ul className="zs-summary__lines">
          {order.items.map((item, i) => (
            <li key={`${item.productId}-${i}`}>
              <div className="zs-summary__media">
                {item.image ? <img src={item.image} alt="" /> : null}
                <span className="zs-summary__qty">{item.quantity}</span>
              </div>
              <div className="zs-summary__name">
                <span>{item.name}</span>
                {item.options && Object.keys(item.options).length > 0 ? (
                  <span className="zs-muted">{Object.values(item.options).join(" · ")}</span>
                ) : null}
              </div>
              <span>{formatMoney(item.lineTotalMinor, order.currency)}</span>
            </li>
          ))}
        </ul>
        <div className="zs-sum-row">
          <span>Subtotal</span>
          <span>{formatMoney(order.subtotalMinor, order.currency)}</span>
        </div>
        <div className="zs-sum-row">
          <span>{order.delivery.name}</span>
          <span>{order.deliveryMinor === 0 ? "Free" : formatMoney(order.deliveryMinor, order.currency)}</span>
        </div>
        <div className="zs-sum-row zs-sum-row--total">
          <span>Total</span>
          <span>{formatMoney(order.totalMinor, order.currency)}</span>
        </div>
        {order.shippingAddress ? (
          <p className="zs-muted zs-ship">
            Delivering to {order.shippingAddress.line1}, {order.shippingAddress.city}
            {order.shippingAddress.state ? `, ${order.shippingAddress.state}` : ""}
          </p>
        ) : order.delivery.pickup ? (
          <p className="zs-muted zs-ship">You'll pick this order up in store.</p>
        ) : null}
      </div>

      <button type="button" className="zs-btn zs-btn--ghost" onClick={() => go(paths.shop)}>
        Continue shopping
      </button>
    </section>
  );
}
